import React, { useState } from 'react';
import { siteConfig } from '../data';
import { Mail, Phone, Send } from 'lucide-react';
import { motion } from 'framer-motion';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
    website_url: '' // Honeypot
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Antispam: if website_url is filled, ignore submission
    if (formData.website_url) return;

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      if (response.ok) {
        alert('Tak for din besked. Vi vender tilbage hurtigst muligt.');
        setFormData({ name: '', email: '', message: '', website_url: '' });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
    }
  };

  return (
    <section id="contact" className="py-24 md:py-32 lg:py-40 bg-white">
      <div className="container mx-auto px-0 sm:px-4">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="mb-24 px-4 sm:px-0"
        >
          <h2 className="text-sm font-black text-slate-400 uppercase tracking-[0.3em] mb-6">Kontakt</h2>
          <h3 className="text-5xl font-black text-slate-900 mb-8 uppercase tracking-tighter">Mød vores team</h3>
          <p className="text-lg text-slate-600 font-medium max-w-2xl">Har du spørgsmål til vores produkter eller ønsker du rådgivning? Vores eksperter sidder klar til at hjælpe dig.</p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 lg:gap-28">
          {/* Team Members */}
          <div className="space-y-12 px-4 sm:px-0">
            {siteConfig.team.map((member, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-slate-50 p-10 md:p-12 rounded-none flex flex-col md:flex-row items-start md:items-center gap-10 group shadow-sm"
              >
                <div className="w-32 h-32 md:w-28 md:h-28 rounded-none overflow-hidden shrink-0 border-2 border-white shadow-lg mb-4 md:mb-0">
                  <img src={member.image} alt={member.name} className="w-full h-full object-cover" />
                </div>
                <div>
                  <h4 className="text-2xl font-black text-slate-900 uppercase tracking-tight">{member.name}</h4>
                  <p className="text-slate-900 font-black mb-4 text-xs uppercase tracking-widest">{member.role}</p>
                  <div className="space-y-2">
                    <a href={`mailto:${member.email}`} className="flex items-center gap-3 text-sm text-black font-bold hover:text-slate-900 transition-colors">
                      <Mail size={16} className="text-black" /> {member.email}
                    </a>
                    <a href={`tel:${member.phone}`} className="flex items-center gap-3 text-sm text-black font-bold hover:text-slate-900 transition-colors">
                      <Phone size={16} className="text-black" /> {member.phone}
                    </a>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Contact Form */}
          <motion.div 
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6 }}
            className="bg-white px-8 py-14 sm:p-12 md:p-16 rounded-none border-t-4 border-slate-900 shadow-2xl"
          >
            <form onSubmit={handleSubmit} className="space-y-10">
              <div className="sr-only absolute" aria-hidden="true">
                <input 
                  type="text" 
                  name="website_url" 
                  value={formData.website_url} 
                  onChange={(e) => setFormData({...formData, website_url: e.target.value})} 
                  tabIndex="-1" 
                  autoComplete="off"
                />
              </div>

              <div>
                <label className="block text-xs font-black text-slate-900 uppercase tracking-widest mb-3">Navn</label>
                <input 
                  type="text" 
                  required
                  placeholder="Dit navn"
                  className="w-full bg-slate-50 px-5 py-4 rounded-none border-none text-slate-900 placeholder-slate-400 focus:ring-2 focus:ring-slate-900 transition-all"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-xs font-black text-slate-900 uppercase tracking-widest mb-3">Email</label>
                <input 
                  type="email" 
                  required
                  placeholder="Din email"
                  className="w-full bg-slate-50 px-5 py-4 rounded-none border-none text-slate-900 placeholder-slate-400 focus:ring-2 focus:ring-slate-900 transition-all"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-xs font-black text-slate-900 uppercase tracking-widest mb-3">Besked</label>
                <textarea 
                  required
                  rows="4"
                  placeholder="Hvordan kan vi hjælpe?"
                  className="w-full bg-slate-50 px-5 py-4 rounded-none border-none text-slate-900 placeholder-slate-400 focus:ring-2 focus:ring-slate-900 transition-all resize-none"
                  value={formData.message}
                  onChange={(e) => setFormData({...formData, message: e.target.value})}
                ></textarea>
              </div>
              <button 
                type="submit"
                className="w-full bg-slate-900 hover:bg-slate-800 text-white font-black py-5 rounded-none flex items-center justify-center gap-3 transition-all transform hover:scale-[1.02] active:scale-[0.98] uppercase tracking-[0.2em]"
              >
                Send besked <Send size={20} />
              </button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
